import { UiSelect } from '@experiences/ui-common';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import {
    Button,
    IconButton,
    InputLabel,
    TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useMemo,
} from 'react';
import {
    useFieldArray,
    useFormContext,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ISAMLProvisioningCondition } from '../../../common/interfaces/cis/saml';
import { SAMLConditionType } from '../../../common/interfaces/cis/saml';

const useStyles = makeStyles(theme =>
    createStyles({
        condition: {
            width: '100%',
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
        },
        labelText: {
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 600,
            marginBottom: '8px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        inputControl: {
            marginBottom: '16px',
            marginRight: '16px',
            minWidth: '230px',
        },
        deleteButton: { marginTop: '32px' },
    }),
);

const samlConditionTypeOptions = {
    [SAMLConditionType.Is]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_IS',
    [SAMLConditionType.IsNot]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_IS_NOT',
    [SAMLConditionType.Contains]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_CONTAINS',
    [SAMLConditionType.NotContains]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_NOT_CONTAINS',
    [SAMLConditionType.IsCaseInsensitive]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_IS_CASE_INSENSITIVE',
    [SAMLConditionType.ContainsCaseInsensitive]: 'CLIENT_SAML_PROVISIONING_CONDITION_TYPE_CONTAINS_CASE_INSENSITIVE',
};

const SAMLProvisioningConditionGenerator: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    const {
        control, formState, register, setValue, watch,
    } = useFormContext();
    const {
        fields, append,
    } = useFieldArray({
        control,
        name: 'conditions',
    });
    const conditions: ISAMLProvisioningCondition[] = useMemo(() => watch('conditions'), [ watch ]);

    const { errors } = formState;

    const addCondition = useCallback(() => {
        append({
            ClaimName: '',
            ConditionType: 'Is',
            Value: '',
        });
    }, [ append ]);

    const removeCondition = useCallback(
        (index: number) => {
            setValue(
                'conditions',
                conditions.filter((_, i) => i !== index),
            );
        },
        [ conditions, setValue ],
    );

    return (
        <>
            {fields.map((field, i) => (
                <li
                    key={field.id}
                    className={classes.condition}>
                    <div className={classes.inputControl}>
                        <InputLabel className={classes.labelText}>
                            {translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_CLAIM' })}
                        </InputLabel>
                        <TextField
                            name={`conditions.${i}.ClaimName`}
                            inputRef={register({ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }) as any}
                            placeholder={translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_CLAIM_PLACEHOLDER' })}
                            defaultValue={field.ClaimName}
                            variant="outlined"
                            fullWidth
                            error={!!errors.Conditions?.[i]?.ClaimName}
                            helperText={errors.Conditions?.[i]?.ClaimName?.message}
                            aria-label={field.ClaimName || translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_CLAIM_PLACEHOLDER' })}
                            data-cy="saml-condition-claim"
                        />
                    </div>
                    <div className={classes.inputControl}>
                        <UiSelect
                            name={`conditions.${i}.ConditionType`}
                            control={control}
                            inputLabel={translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_RELATIONSHIP' })}
                            placeholder={translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_RELATIONSHIP_PLACEHOLDER' })}
                            options={samlConditionTypeOptions}
                            defaultValue={field.ConditionType}
                            fullWidth
                            required
                            error={!!errors.Conditions?.[i]?.ConditionType}
                            helperText={errors.Conditions?.[i]?.ConditionType?.message}
                            dataCy="ui-select-condition-type"
                        />
                    </div>
                    <div className={classes.inputControl}>
                        <InputLabel className={classes.labelText}>
                            {translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_VALUE' })}
                        </InputLabel>
                        <TextField
                            name={`conditions.${i}.Value`}
                            inputRef={register({ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }) as any}
                            placeholder={translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_VALUE_PLACEHOLDER' })}
                            defaultValue={field.Value}
                            variant="outlined"
                            fullWidth
                            error={!!errors.Conditions?.[i]?.Value}
                            helperText={errors.Conditions?.[i]?.Value?.message}
                            aria-label={field.ClaimName || translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_VALUE_PLACEHOLDER' })}
                            data-cy="saml-condition-value"
                        />
                    </div>
                    <IconButton
                        className={classes.deleteButton}
                        onClick={() => removeCondition(i)}
                        data-cy="saml-condition-remove">
                        <DeleteForeverOutlinedIcon />
                    </IconButton>
                </li>
            ))}
            <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={() => addCondition()}
                data-cy="add-rule-condition-button">
                {translate({ id: 'CLIENT_SAML_PROVISIONING_RULES_ADD_RULE_BUTTON' })}
            </Button>
        </>
    );
};

export default SAMLProvisioningConditionGenerator;
